import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { Undo } from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/EnterpriseAuthContext';
import {
  mergeProjectInto,
  moveSessionToProject,
  renameProject,
  undoAutoFiling,
} from '../../services/autopilotService';

/**
 * Tell the rest of the app that sessions, projects or customers changed (AppContext reloads).
 * @param {'session'|'project'|'customer'} kind - What changed.
 * @returns {void}
 */
export function announceDataChanged(kind) {
  try {
    window.dispatchEvent(new CustomEvent('quillai:data-changed', { detail: { kind } }));
  } catch {
    // Older browsers without CustomEvent: the next page load shows the change.
  }
}

/**
 * "<Project> is in use" after Undo kept a project autopilot created: rename it, or merge it into
 * another project. A component of its own so a page can keep it open after the banner that ran
 * Undo has gone (the session page renders it itself; see FilingControls `onProjectInUse`).
 * @param {object} props - Props.
 * @param {{id: string, name: string}|null} props.inUse - The kept project (null: closed).
 * @param {() => void} props.onClose - Close.
 * @param {(event: {kind: 'renamed'|'merged', message: string}) => void} [props.onChanged] - After a change.
 * @returns {JSX.Element} Dialog.
 */
export function ProjectInUseDialog({ inUse, onClose, onChanged }) {
  const { state: authState } = useAuth();
  const { state: appState, actions } = useApp();
  const accountId = authState.activeAccount?.id || null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [mergeTargetId, setMergeTargetId] = useState('');
  const inUseId = inUse?.id || null;
  const inUseName = inUse?.name || '';

  useEffect(() => {
    setRenameValue(inUseName);
    setMergeTargetId('');
    setError('');
  }, [inUseId, inUseName]);

  const projects = useMemo(
    () => [...(appState.projects || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [appState.projects]
  );

  const finish = useCallback(async (kind, message) => {
    onClose();
    if (typeof onChanged === 'function') onChanged({ kind, message });
    await Promise.allSettled([actions.loadProjects?.(), actions.loadCustomers?.()]);
    announceDataChanged('project');
  }, [actions, onChanged, onClose]);

  const handleRename = useCallback(async () => {
    if (!inUse) return;
    setBusy(true);
    setError('');
    try {
      await renameProject({ projectId: inUse.id, accountId, name: renameValue });
      await finish('renamed', 'Project renamed.');
    } catch (err) {
      setError(err.message || 'We could not rename the project.');
    } finally {
      setBusy(false);
    }
  }, [inUse, accountId, renameValue, finish]);

  const handleMerge = useCallback(async () => {
    if (!inUse || !mergeTargetId) return;
    setBusy(true);
    setError('');
    try {
      await mergeProjectInto({ fromProjectId: inUse.id, toProjectId: mergeTargetId, accountId });
      const name = projects.find((p) => p.id === mergeTargetId)?.name || 'the other project';
      await finish('merged', `Merged into ${name}.`);
    } catch (err) {
      setError(err.message || 'We could not merge the projects.');
    } finally {
      setBusy(false);
    }
  }, [inUse, mergeTargetId, accountId, projects, finish]);

  return (
    <Dialog open={Boolean(inUse)} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth aria-labelledby="autopilot-inuse-title">
      <DialogTitle id="autopilot-inuse-title">{inUse?.name || 'This project'} is in use</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Other meetings, requirements or documents now use this project, so Quill kept it with everything in it. Rename it, or merge it into another project.
        </DialogContentText>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField label="New name" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} sx={{ flex: '1 1 220px' }} />
            <Button variant="outlined" onClick={handleRename} disabled={busy || !renameValue.trim()} sx={{ minHeight: 40 }}>Rename</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField select label="Merge into" value={mergeTargetId} onChange={(e) => setMergeTargetId(e.target.value)} sx={{ flex: '1 1 220px' }}>
              {projects.filter((p) => p.id !== inUse?.id).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            <Button variant="outlined" onClick={handleMerge} disabled={busy || !mergeTargetId} sx={{ minHeight: 40 }}>Merge</Button>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Keep as it is</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Change / Undo for a meeting autopilot filed. Undo moves the meeting back to unfiled and
 * deletes what autopilot created for it when nothing else uses it; otherwise it offers to rename
 * the project or merge it into another one. Change moves the meeting to a project the user picks.
 * @param {object} props - Props.
 * @param {string} props.sessionId - Session id.
 * @param {string} props.sessionName - Session name (for dialog copy).
 * @param {object|null} props.filing - Normalized filing (null: only Change is offered).
 * @param {boolean} [props.canUndo] - Show Undo.
 * @param {string|null} [props.currentProjectId] - Where the meeting is now.
 * @param {string} [props.changeLabel] - Label of the Change button.
 * @param {(event: {kind: 'undone'|'moved'|'renamed'|'merged', message: string}) => void} [props.onChanged] - Called after a change (before the app data reloads).
 * @param {(project: {id: string, name: string}) => void} [props.onProjectInUse] - When set, the page shows the "in use" dialog itself (it outlives this component); otherwise it is shown here.
 * @returns {JSX.Element} Buttons and their dialogs.
 */
export default function FilingControls({
  sessionId,
  sessionName,
  filing,
  canUndo = false,
  currentProjectId = null,
  changeLabel = 'Change',
  onChanged,
  onProjectInUse,
}) {
  const { state: authState } = useAuth();
  const { state: appState, actions } = useApp();
  const accountId = authState.activeAccount?.id || null;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [inUse, setInUse] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [targetCustomerId, setTargetCustomerId] = useState('');
  const [targetProjectId, setTargetProjectId] = useState('');

  const projects = useMemo(
    () => [...(appState.projects || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [appState.projects]
  );
  const customers = useMemo(
    () => [...(appState.customers || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [appState.customers]
  );
  const projectChoices = projects.filter((p) => p.id !== currentProjectId && (!targetCustomerId || p.customer_id === targetCustomerId));

  const reload = useCallback(async () => {
    await Promise.allSettled([actions.loadProjects?.(), actions.loadCustomers?.()]);
    announceDataChanged('session');
  }, [actions]);

  const report = useCallback((kind, message) => {
    if (typeof onChanged === 'function') onChanged({ kind, message });
  }, [onChanged]);

  const handleUndo = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const out = await undoAutoFiling({ sessionId, accountId, filing });
      setConfirmOpen(false);
      // Offer rename / merge and tell the page first: reloading the app data can unmount this
      // component (the session page re-renders the meeting as unfiled).
      if (out.status === 'already_moved') {
        report('undone', 'This meeting was already moved, so nothing was undone.');
      } else if (out.projectInUse) {
        if (typeof onProjectInUse === 'function') onProjectInUse(out.projectInUse);
        else setInUse(out.projectInUse);
        report('undone', 'Moved back to unfiled. The project is used by other work, so it was kept with its requirements.');
      } else {
        const removed = [out.deletedProject && 'the new project', out.deletedCustomer && 'the new customer'].filter(Boolean);
        const kept = !out.deletedProject && out.keptRequirements > 0 ? ' Its requirements stay in the project.' : '';
        report('undone', `Moved back to unfiled${removed.length ? ` and removed ${removed.join(' and ')}` : ''}.${kept}`);
      }
      await reload();
    } catch (err) {
      setError(err.message || 'We could not undo this.');
    } finally {
      setBusy(false);
    }
  }, [sessionId, accountId, filing, reload, report, onProjectInUse]);

  const handleMove = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      await moveSessionToProject({ sessionId, projectId: targetProjectId });
      setChangeOpen(false);
      const name = projects.find((p) => p.id === targetProjectId)?.name || 'the project';
      report('moved', `Moved to ${name}.`);
      await reload();
    } catch (err) {
      setError(err.message || 'We could not move this meeting.');
    } finally {
      setBusy(false);
    }
  }, [sessionId, targetProjectId, projects, reload, report]);

  const createdSomething = Boolean(filing?.createdProject || filing?.createdCustomer);
  const projectName = filing?.projectName || 'that project';
  const undoMessage = [
    `"${sessionName || 'This meeting'}" moves back to unfiled.`,
    createdSomething
      ? `If nothing else uses ${projectName}, Quill deletes it${filing?.createdCustomer ? ' and the customer it created' : ''}, with the requirements it pulled from this meeting. If anything else uses it, or you edited those requirements, everything is kept.`
      : `${projectName} and all its requirements, including any Quill pulled from this meeting, are kept.`,
  ].join(' ');

  return (
    <>
      <Button size="small" color="inherit" onClick={() => { setError(''); setTargetCustomerId(''); setTargetProjectId(''); setChangeOpen(true); }}>
        {changeLabel}
      </Button>
      {canUndo && filing?.projectId && (
        <Button size="small" color="inherit" startIcon={<Undo fontSize="small" />} onClick={() => { setError(''); setConfirmOpen(true); }}>
          Undo
        </Button>
      )}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth="xs" fullWidth disableRestoreFocus aria-labelledby="autopilot-undo-title">
        <DialogTitle id="autopilot-undo-title">Undo filing?</DialogTitle>
        <DialogContent>
          <DialogContentText>{undoMessage}</DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleUndo} disabled={busy} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Undo />}>
            Undo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={changeOpen} onClose={() => !busy && setChangeOpen(false)} maxWidth="xs" fullWidth disableRestoreFocus aria-labelledby="autopilot-change-title">
        <DialogTitle id="autopilot-change-title">File this meeting under</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Customer"
              value={targetCustomerId}
              onChange={(e) => { setTargetCustomerId(e.target.value); setTargetProjectId(''); }}
            >
              <MenuItem value="">All customers</MenuItem>
              {customers.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Project"
              value={targetProjectId}
              onChange={(e) => setTargetProjectId(e.target.value)}
              helperText={projectChoices.length ? '' : 'No other projects here yet.'}
            >
              {projectChoices.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleMove} disabled={busy || !targetProjectId}>
            Move
          </Button>
        </DialogActions>
      </Dialog>

      <ProjectInUseDialog inUse={inUse} onClose={() => setInUse(null)} onChanged={onChanged} />
    </>
  );
}
